// Aba "Dados do fornecedor": quem é, como se fala com ele, como ele entrega e
// o que o sistema apuraria sobre ele se houvesse recebimento.
import React from "react";
import * as Casca from "../../../../styles/components/configuration/modals/_casca";
import * as Estilo from "../../../../styles/components/configuration/modals/SupplierModal";
import {
  CANAIS_PREFERIDOS,
  DIAS_DA_SEMANA,
  FRETE_TIPOS,
  FormularioDeFornecedor,
  INDICADORES_CALCULADOS,
} from "./tipos";

interface Props {
  form: FormularioDeFornecedor;
  onChange: (campo: keyof FormularioDeFornecedor, valor: any) => void;
  /** WhatsApp validado é fato do envio, não escolha: chega pronto de fora. */
  whatsappValidado: boolean;
}

export const DadosDoFornecedor: React.FC<Props> = ({
  form,
  onChange,
  whatsappValidado,
}) => {
  const alternarDia = (
    campo: "janelaAtendimentoDias" | "diasDeEntrega",
    dia: string
  ) => {
    const atual = form[campo];
    onChange(
      campo,
      atual.indexOf(dia) >= 0
        ? atual.filter((item) => item !== dia)
        : atual.concat(dia)
    );
  };

  return (
    <>
      <Casca.FormSection>
        <Casca.SectionTitle>Identificação</Casca.SectionTitle>
        <Casca.FormGrid>
          <Casca.LabelInputContainer>
            <Casca.Label>Nome *</Casca.Label>
            <Casca.Input
              type="text"
              value={form.nome}
              onChange={(e) => onChange("nome", e.target.value)}
              placeholder="Razão social ou nome pelo qual você o chama"
            />
          </Casca.LabelInputContainer>

          <Casca.LabelInputContainer>
            <Casca.Label>CNPJ</Casca.Label>
            <Casca.Input
              type="text"
              value={form.cnpj}
              onChange={(e) => onChange("cnpj", e.target.value)}
              placeholder="00.000.000/0000-00"
            />
          </Casca.LabelInputContainer>

          <Casca.LabelInputContainer>
            <Casca.Label>Contato</Casca.Label>
            <Casca.Input
              type="text"
              value={form.contato}
              onChange={(e) => onChange("contato", e.target.value)}
              placeholder="Com quem você fala"
            />
          </Casca.LabelInputContainer>

          <Casca.LabelInputContainer>
            <Casca.Label>E-mail</Casca.Label>
            <Casca.Input
              type="email"
              value={form.email}
              onChange={(e) => onChange("email", e.target.value)}
              placeholder="pedidos@fornecedor.com.br"
            />
          </Casca.LabelInputContainer>

          <Casca.LabelInputContainer>
            <Casca.Label>Telefone</Casca.Label>
            <Casca.Input
              type="text"
              value={form.telefone}
              onChange={(e) => onChange("telefone", e.target.value)}
              placeholder="(00) 00000-0000"
            />
          </Casca.LabelInputContainer>

          <Casca.LabelInputContainer fullWidth>
            <Casca.Label>Endereço</Casca.Label>
            <Casca.Input
              type="text"
              value={form.endereco}
              onChange={(e) => onChange("endereco", e.target.value)}
              placeholder="Rua, número, bairro, cidade"
            />
          </Casca.LabelInputContainer>
        </Casca.FormGrid>
      </Casca.FormSection>

      <Casca.FormSection>
        <Casca.SectionTitle>Como o pedido chega até ele</Casca.SectionTitle>
        <Casca.FormGrid>
          <Casca.LabelInputContainer>
            <Casca.Label>Canal preferido</Casca.Label>
            <Casca.Select
              value={form.canalPreferido}
              onChange={(e) => onChange("canalPreferido", e.target.value)}
            >
              {CANAIS_PREFERIDOS.map((canal) => (
                <option key={canal.valor} value={canal.valor}>
                  {canal.rotulo}
                </option>
              ))}
            </Casca.Select>
          </Casca.LabelInputContainer>

          <Casca.LabelInputContainer>
            <Casca.Label>
              WhatsApp validado
              <Casca.InfoBadge>somente leitura</Casca.InfoBadge>
            </Casca.Label>
            {/* Não é caixa de seleção de propósito: quem valida o número é o
                PRIMEIRO ENVIO bem-sucedido, não a opinião de quem cadastra.
                Marcar "validado" na mão faria o sistema disparar pedido para um
                número que ninguém provou que responde. */}
            <Estilo.ValorCalculado>
              {whatsappValidado ? "Sim" : "Ainda não"}
            </Estilo.ValorCalculado>
            <Estilo.ExplicacaoCurta>
              Vira "Sim" sozinho quando o primeiro envio para este número for
              entregue. Não há como marcar por aqui.
            </Estilo.ExplicacaoCurta>
          </Casca.LabelInputContainer>

          <Casca.LabelInputContainer>
            <Casca.Label>Janela de consolidação (horas)</Casca.Label>
            <Casca.Input
              type="number"
              min="0"
              step="1"
              value={form.janelaConsolidacaoHoras}
              onChange={(e) =>
                onChange("janelaConsolidacaoHoras", e.target.value)
              }
              placeholder="Ex.: 4"
            />
            <Estilo.ExplicacaoCurta>
              Quanto tempo juntar as necessidades antes de mandar um pedido só.
            </Estilo.ExplicacaoCurta>
          </Casca.LabelInputContainer>
        </Casca.FormGrid>
      </Casca.FormSection>

      <Casca.FormSection>
        <Casca.SectionTitle>Condições comerciais</Casca.SectionTitle>
        <Casca.FormGrid>
          <Casca.LabelInputContainer>
            <Casca.Label>Prazo de entrega (dias)</Casca.Label>
            <Casca.Input
              type="number"
              min="0"
              step="1"
              value={form.prazoEntregaDias}
              onChange={(e) => onChange("prazoEntregaDias", e.target.value)}
              placeholder="Ex.: 2"
            />
          </Casca.LabelInputContainer>

          <Casca.LabelInputContainer>
            <Casca.Label>Condições de pagamento</Casca.Label>
            <Casca.Input
              type="text"
              value={form.condicoesPagamento}
              onChange={(e) => onChange("condicoesPagamento", e.target.value)}
              placeholder="Ex.: 28 dias após a entrega"
            />
          </Casca.LabelInputContainer>

          <Casca.LabelInputContainer>
            <Casca.Label>Pedido mínimo (R$)</Casca.Label>
            <Casca.Input
              type="number"
              min="0"
              step="0.01"
              value={form.pedidoMinimoReais}
              onChange={(e) => onChange("pedidoMinimoReais", e.target.value)}
              placeholder="0,00"
            />
          </Casca.LabelInputContainer>

          <Casca.LabelInputContainer>
            <Casca.Label>Frete</Casca.Label>
            <Casca.Select
              value={form.freteTipo}
              onChange={(e) => onChange("freteTipo", e.target.value)}
            >
              {FRETE_TIPOS.map((tipo) => (
                <option key={tipo.valor} value={tipo.valor}>
                  {tipo.rotulo}
                </option>
              ))}
            </Casca.Select>
          </Casca.LabelInputContainer>

          {/* Um campo por vez: o R$ de "valor fixo" é custo, o R$ de "grátis
              acima de" é piso de pedido. Mostrar os dois juntos convida a
              preencher os dois e deixar o sistema escolher. */}
          {form.freteTipo === "valorFixo" && (
            <Casca.LabelInputContainer>
              <Casca.Label>Valor do frete (R$)</Casca.Label>
              <Casca.Input
                type="number"
                min="0"
                step="0.01"
                value={form.freteValor}
                onChange={(e) => onChange("freteValor", e.target.value)}
                placeholder="0,00"
              />
            </Casca.LabelInputContainer>
          )}

          {form.freteTipo === "gratisAcimaDe" && (
            <Casca.LabelInputContainer>
              <Casca.Label>Frete grátis acima de (R$)</Casca.Label>
              <Casca.Input
                type="number"
                min="0"
                step="0.01"
                value={form.freteGratisAcima}
                onChange={(e) => onChange("freteGratisAcima", e.target.value)}
                placeholder="0,00"
              />
            </Casca.LabelInputContainer>
          )}
        </Casca.FormGrid>
      </Casca.FormSection>

      <Casca.FormSection>
        <Casca.SectionTitle>Quando ele atende e quando entrega</Casca.SectionTitle>

        <Casca.Label>Dias em que atende</Casca.Label>
        <Estilo.LinhaDeDias>
          {DIAS_DA_SEMANA.map((dia) => (
            <Estilo.BotaoDeDia
              key={`atendimento-${dia.valor}`}
              type="button"
              ativo={form.janelaAtendimentoDias.indexOf(dia.valor) >= 0}
              onClick={() => alternarDia("janelaAtendimentoDias", dia.valor)}
            >
              {dia.rotulo}
            </Estilo.BotaoDeDia>
          ))}
        </Estilo.LinhaDeDias>

        <Casca.FormGrid style={{ marginTop: "var(--sp-16)" }}>
          <Casca.LabelInputContainer>
            <Casca.Label>Atende das</Casca.Label>
            <Casca.Input
              type="time"
              value={form.janelaAtendimentoInicio}
              onChange={(e) =>
                onChange("janelaAtendimentoInicio", e.target.value)
              }
            />
          </Casca.LabelInputContainer>

          <Casca.LabelInputContainer>
            <Casca.Label>até</Casca.Label>
            <Casca.Input
              type="time"
              value={form.janelaAtendimentoFim}
              onChange={(e) => onChange("janelaAtendimentoFim", e.target.value)}
            />
          </Casca.LabelInputContainer>
        </Casca.FormGrid>

        <Casca.Label style={{ marginTop: "var(--sp-16)" }}>
          Dias em que entrega
        </Casca.Label>
        <Estilo.LinhaDeDias>
          {DIAS_DA_SEMANA.map((dia) => (
            <Estilo.BotaoDeDia
              key={`entrega-${dia.valor}`}
              type="button"
              ativo={form.diasDeEntrega.indexOf(dia.valor) >= 0}
              onClick={() => alternarDia("diasDeEntrega", dia.valor)}
            >
              {dia.rotulo}
            </Estilo.BotaoDeDia>
          ))}
        </Estilo.LinhaDeDias>
        <Estilo.NotaDeCampo>
          Atender e entregar são coisas diferentes: dá para pedir na sexta e a
          entrega só sair na segunda.
        </Estilo.NotaDeCampo>
      </Casca.FormSection>

      <Casca.FormSection>
        <Casca.SectionTitle>Desempenho apurado</Casca.SectionTitle>
        <Estilo.GradeDeCalculados>
          {INDICADORES_CALCULADOS.map((indicador) => (
            <Estilo.CampoCalculado key={indicador.chave}>
              <Estilo.TituloDoCalculado>
                {indicador.rotulo}
                <Casca.InfoBadge>calculado</Casca.InfoBadge>
              </Estilo.TituloDoCalculado>
              {/* Travessão, nunca zero: o número que não foi medido não é
                  "zero", é "não medido". */}
              <Estilo.ValorCalculado>—</Estilo.ValorCalculado>
              <Estilo.ExplicacaoCurta>
                {indicador.depende}
              </Estilo.ExplicacaoCurta>
            </Estilo.CampoCalculado>
          ))}
        </Estilo.GradeDeCalculados>
        <Estilo.NotaDeCampo>
          Os quatro só passam a ter valor quando existir registro de recebimento
          (o que chegou, quando chegou e por quanto). Enquanto não existir, a
          tela mostra travessão em vez de um número inventado.
        </Estilo.NotaDeCampo>
      </Casca.FormSection>
    </>
  );
};
